import InfoOutlined from "@mui/icons-material/InfoOutlined";
import React from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import ErrorDisplay from "../../components/ErrorDisplay/ErrorDisplay";
import TableHeader from "../../components/Headers/TableHeader";
import SkeletonText from "../../components/Skeletons/SkeletonText";
import Tooltip from "../../components/Tooltip/Tooltip";
import { useClientConfiguration } from "../../data/global/useProductConfiguration";
import { usePayerLandscapeState } from "../../data/payerLandscape/hooks";
import { PlanData } from "../../generate";

interface Props {
  client: string;
  loading: boolean;
  planData?: PlanData;
  error?: Error;
  displayKeys?: Array<keyof PlanData>;
}

type PlanDataKeys = keyof PlanData;

// Define row configuration type
interface RowConfig {
  dataKey: PlanDataKeys;
  labelKey: string | string[];
  format?: NumericFormatProps;
  clientFormats?: Record<string, NumericFormatProps>;
}

interface CellProps {
  dataKey: PlanDataKeys;
  format?: NumericFormatProps;
  align?: string;
  loading: boolean;
  planData: PlanData | undefined;
}

const Cell: React.FC<CellProps> = ({ dataKey, format, align = "left", loading, planData }) => {
  const { t } = useTranslation();
  if (loading) {
    return <SkeletonText align={align} />;
  }
  if (!planData || planData[dataKey] === undefined || planData[dataKey] === null) {
    return <span>{t("common.na")}</span>;
  }
  if (format) {
    return <NumericFormat value={planData[dataKey]} displayType={"text"} {...format} />;
  }
  return <span>{planData[dataKey]}</span>;
};

interface TableSectionProps {
  rows: RowConfig[];
  isRightSection?: boolean;
  hasRightRows: boolean;
  loading: boolean;
  planData: PlanData | undefined;
}

const TableSection: React.FC<TableSectionProps> = ({
  rows,
  isRightSection = false,
  hasRightRows,
  loading,
  planData,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={`${isRightSection ? "md:w-1/2" : hasRightRows ? "md:w-1/2" : "w-full"} ${
        isRightSection ? "" : hasRightRows ? "md:border-r-2" : ""
      } border-rules divide-y divide-rules`}
    >
      <div className={`${isRightSection ? "hidden md:flex" : "flex"} bg-rules`}>
        <div className="w-2/3 p-3 font-bold">{t("planProfile.metric")}</div>
        <div className="w-1/3 p-3 font-bold text-right">{t("planProfile.data")}</div>
      </div>

      {rows.map((row, index) => (
        <div className="flex" key={`${row.dataKey}-${index}`}>
          <div className="w-2/3 p-3">{Array.isArray(row.labelKey) ? t(row.labelKey) : t(row.labelKey)}</div>
          <div className="w-1/3 p-3 text-right">
            <Cell dataKey={row.dataKey} align="right" format={row.format} loading={loading} planData={planData} />
          </div>
        </div>
      ))}
    </div>
  );
};

interface PlanTableProps {
  leftRows: RowConfig[];
  rightRows: RowConfig[];
  loading: boolean;
  planData: PlanData | undefined;
}

const PlanTable: React.FC<PlanTableProps> = ({ leftRows, rightRows, loading, planData }) => (
  <div className="md:flex w-full border-2 border-rules">
    <TableSection rows={leftRows} hasRightRows={rightRows.length > 0} loading={loading} planData={planData} />
    {rightRows.length > 0 && (
      <TableSection rows={rightRows} isRightSection={true} hasRightRows={true} loading={loading} planData={planData} />
    )}
  </div>
);

const DisplayError: React.FC<{ error: Error }> = ({ error }) => {
  const { t } = useTranslation();
  return (
    <div className="w-full border-2 border-rules">
      <div className="flex">
        <div className="md:w-1/2 md:border-r-2 border-rules divide-y divide-rules flex-grow">
          <div className="flex bg-rules">
            <div className="w-2/3 p-3 font-bold">{t("planProfile.metric")}</div>
            <div className="w-1/3 p-3 font-bold text-right">{t("planProfile.data")}</div>
          </div>
        </div>
        <div className="hidden md:block md:w-1/2 md:border-r-2 border-rules divide-y divide-rules">
          <div className="flex bg-rules">
            <div className="w-2/3 p-3 font-bold">{t("planProfile.metric")}</div>
            <div className="w-1/3 p-3 font-bold text-right">{t("planProfile.data")}</div>
          </div>
        </div>
      </div>
      <div className="py-20 md:pt-3 md:pb-2">
        <ErrorDisplay error={error} />
      </div>
    </div>
  );
};

const PlanDataTable: React.FC<Props> = ({ client, loading, planData, error, displayKeys }) => {
  const { t } = useTranslation();
  const {
    sharedFilters: { timePeriod },
  } = usePayerLandscapeState();
  const config = useClientConfiguration(client);
  const { hidePlanDataTooltip } = config;

  // Define all possible rows with their translations and formats
  const allRowsConfig: Partial<Record<PlanDataKeys, RowConfig>> = {
    totalLives: {
      dataKey: "totalLives",
      labelKey: "planProfile.totalLives",
      format: { thousandSeparator: true },
    },
    percentBOB: {
      dataKey: "percentBOB",
      labelKey: [`planProfile.percentBob.${client}`, `planProfile.percentBob.default`],
      format: { suffix: "%", decimalScale: 1, fixedDecimalScale: true },
    },
    reportingHcps: {
      dataKey: "reportingHcps",
      labelKey: [`planProfile.noReportingHcps.${client}`, `planProfile.noReportingHcps.default`],
      format: { thousandSeparator: true },
      clientFormats: {
        // Add client-specific formats here if needed
        PAXLOVID: { suffix: "%", decimalScale: 1, fixedDecimalScale: true },
        PAXLOVID_PUCC: { suffix: "%", decimalScale: 1, fixedDecimalScale: true },
      },
    },
    relStatus: {
      dataKey: "relStatus",
      labelKey: `planProfile.relativeStatus.${client}`,
    },
    productTrx: {
      dataKey: "productTrx",
      labelKey: `planProfile.trxWithProduct.${timePeriod}.${client}`,
      format: { thousandSeparator: true },
    },
    marketTrx: {
      dataKey: "marketTrx",
      labelKey: `planProfile.marketTrx.${timePeriod}.${client}`,
      format: { thousandSeparator: true },
    },
    marketShare: {
      dataKey: "marketShare",
      labelKey: `planProfile.marketShare.${timePeriod}.${client}`,
      format: { suffix: "%", decimalScale: 1, fixedDecimalScale: true },
    },
  };

  const getRowConfig = (key: PlanDataKeys): RowConfig | undefined => {
    const config = allRowsConfig[key];
    if (!config) return undefined;

    const clientSpecificFormat = config.clientFormats?.[client];

    return {
      ...config,
      format: clientSpecificFormat ? { ...config.format, ...clientSpecificFormat } : config.format,
    };
  };

  // Use provided keys or default
  const keysToDisplay = displayKeys || [];

  // Filter and map to get visible rows
  const visibleRows = keysToDisplay
    .map((key) => getRowConfig(key))
    .filter((config): config is RowConfig => config !== undefined);

  // Create optimal distribution of rows across columns
  const distributeRows = (rows: RowConfig[]) => {
    const totalRows = rows.length;

    // If 3 or fewer rows, use a single column
    if (totalRows <= 3) {
      return { leftRows: rows, rightRows: [] };
    }

    // Otherwise, calculate optimal split point
    const midPoint = Math.ceil(totalRows / 2);

    return {
      leftRows: rows.slice(0, midPoint),
      rightRows: rows.slice(midPoint),
    };
  };

  const { leftRows, rightRows } = distributeRows(visibleRows);

  return (
    <div className="pb-6">
      <TableHeader title={t("planProfile.planData")}>
        {!hidePlanDataTooltip && (
          <Tooltip
            placement="bottom-end"
            tip={t([`planProfile.planDataTooltip.${client}`, `planProfile.planDataTooltip.default`]) as string}
          >
            <InfoOutlined className="text-primary" />
          </Tooltip>
        )}
      </TableHeader>
      {error ? (
        <DisplayError error={error} />
      ) : (
        <PlanTable leftRows={leftRows} rightRows={rightRows} loading={loading} planData={planData} />
      )}
    </div>
  );
};

export default PlanDataTable;
